// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Cairn — the canvas renderer + juice. The cairn is drawn tip-up on a deep-indigo night; each
// stone is a warm sandstone tile with its rune (A–K) inked on it. A stone that can be taken now
// (exposed AND one rune from the hand) lifts, brightens and gains a soft amber ring; a blocked
// stone sits under a shadow scrim. Taking a stone flies it to the hand with a spark and a chain
// pop; drawing slides a scree stone up and snaps the heat meter back. Clearing flashes green;
// running out of draws (Scree) shakes and flashes coral. No gameplay logic lives here — it reads
// GameState (exposure/legality re-derived each frame) and paints. Particles + flyers are capped.

import { computeLayout, type CairnLayout, type Slot, type StoneRect } from './layout';
import { canTake, isExposed, type GameEvent, type GameState } from './game';
import { PALETTE, STONE_HUES } from './palette';
import { rankLabel } from './dom';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  col: string;
  r: number;
}
interface Flyer {
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  w: number;
  h: number;
  rank: number;
  hue: number;
  t: number;
  dur: number;
}
interface Pop {
  x: number;
  y: number;
  text: string;
  life: number;
  col: string;
}

export interface RenderOpts {
  dtMs: number;
  reducedMotion: boolean;
}

const MAX_PARTICLES = 160;

export class Board {
  private ctx: CanvasRenderingContext2D;
  private dpr = 1;
  private cw = 0;
  private ch = 0;
  private layout: CairnLayout | null = null;
  private pyH = 6;

  private particles: Particle[] = [];
  private flyers: Flyer[] = [];
  private pops: Pop[] = [];
  private shake = 0;
  private pulse = 0;
  private clearFlash = 0;
  private redFlash = 0;
  private selected: number | null = null;
  private drawFlash = 0;

  constructor(private canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    this.ctx = ctx;
  }

  setBoard(state: GameState): void {
    this.pyH = state.board.h;
    this.particles = [];
    this.flyers = [];
    this.pops = [];
    this.selected = null;
    this.relayout();
  }

  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) return; // ignore a transient 0-size measure
    this.dpr = Math.min(window.devicePixelRatio || 1, 2.5);
    this.cw = rect.width;
    this.ch = rect.height;
    this.canvas.width = Math.round(rect.width * this.dpr);
    this.canvas.height = Math.round(rect.height * this.dpr);
    this.relayout();
  }

  private relayout(): void {
    if (this.cw < 2 || this.ch < 2) return;
    this.layout = computeLayout(this.cw, this.ch, this.pyH);
  }

  setSelected(id: number | null): void {
    this.selected = id;
  }

  // ── juice ────────────────────────────────────────────────────────────────
  onEvents(events: GameEvent[], state: GameState, reduced: boolean): void {
    const L = this.layout;
    for (const e of events) {
      if (e.t === 'take') {
        if (L) {
          const from = L.stones[e.id];
          const hue = state.board.stones[e.id].hue;
          this.flyers.push({ fromX: from.cx, fromY: from.cy, toX: L.hand.cx, toY: L.hand.cy, w: from.w, h: from.h, rank: e.rank, hue, t: 0, dur: reduced ? 0.001 : 0.16 });
          this.burst({ x: L.hand.cx, y: L.hand.cy }, STONE_HUES[hue % 4], reduced ? 3 : 8, from.w * 0.06);
        }
      } else if (e.t === 'chain') {
        if (L && e.len >= 2) this.pops.push({ x: L.hand.cx, y: L.hand.cy - L.hand.h * 0.7, text: `×${Math.min(e.len, 99)}`, life: 1, col: PALETTE.spark });
      } else if (e.t === 'draw') {
        if (L) {
          this.flyers.push({ fromX: L.stock.cx, fromY: L.stock.cy, toX: L.hand.cx, toY: L.hand.cy, w: L.stock.w, h: L.stock.h, rank: e.rank, hue: state.hue, t: 0, dur: reduced ? 0.001 : 0.15 });
          this.drawFlash = 1;
        }
      } else if (e.t === 'clearCairn') {
        this.clearFlash = 1;
        if (L && !reduced) for (let i = 0; i < 5; i++) this.burst({ x: this.cw * (0.2 + i * 0.15), y: this.ch * 0.4 }, PALETTE.clear, 6, this.cw * 0.004);
        if (!reduced) this.shake = Math.max(this.shake, 4);
      } else if (e.t === 'buried') {
        this.redFlash = 1;
        if (!reduced) this.shake = Math.max(this.shake, 10);
      } else if (e.t === 'next') {
        this.setBoard(state);
      }
    }
  }

  private burst(at: { x: number; y: number }, col: string, n: number, speed: number): void {
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + Math.random() * 0.7;
      const s = speed * (0.5 + Math.random() * 1.1);
      this.particles.push({ x: at.x, y: at.y, vx: Math.cos(a) * s, vy: Math.sin(a) * s - speed * 0.4, life: 0, max: 0.4 + Math.random() * 0.5, col, r: 1.6 + Math.random() * 2.6 });
    }
    if (this.particles.length > MAX_PARTICLES) this.particles.splice(0, this.particles.length - MAX_PARTICLES);
  }

  frame(s: GameState, opts: RenderOpts): void {
    const ctx = this.ctx;
    if (!this.layout) this.relayout();
    const L = this.layout;
    if (!L) return;
    const dt = Math.min(opts.dtMs, 60) / 1000;
    this.pulse += dt;

    if (this.shake > 0) this.shake = Math.max(0, this.shake - dt * 26);
    if (this.clearFlash > 0) this.clearFlash = Math.max(0, this.clearFlash - dt * 1.3);
    if (this.redFlash > 0) this.redFlash = Math.max(0, this.redFlash - dt * 1.5);
    if (this.drawFlash > 0) this.drawFlash = Math.max(0, this.drawFlash - dt * 3);
    for (const p of this.particles) {
      p.life += dt;
      p.x += p.vx;
      p.y += p.vy;
      p.vx *= 0.9;
      p.vy = p.vy * 0.9 + 0.6;
    }
    this.particles = this.particles.filter((p) => p.life < p.max);
    for (const f of this.flyers) f.t += dt;
    this.flyers = this.flyers.filter((f) => f.t < f.dur);
    for (const p of this.pops) p.life -= dt * 1.6;
    this.pops = this.pops.filter((p) => p.life > 0);

    ctx.save();
    ctx.scale(this.dpr, this.dpr);
    ctx.clearRect(0, 0, this.cw, this.ch);
    if (this.shake > 0.2) {
      const a = this.pulse * 60;
      ctx.translate(Math.sin(a * 1.7) * this.shake * 0.6, Math.cos(a * 2.3) * this.shake * 0.6);
    }
    if (this.redFlash > 0) {
      ctx.fillStyle = `rgba(255,125,109,${0.2 * this.redFlash})`;
      ctx.fillRect(-40, -40, this.cw + 80, this.ch + 80);
    }
    if (this.clearFlash > 0) {
      ctx.fillStyle = `rgba(121,230,166,${0.16 * this.clearFlash})`;
      ctx.fillRect(-40, -40, this.cw + 80, this.ch + 80);
    }

    this.drawPyramid(ctx, s, L);
    this.drawStrip(ctx, s, L);
    this.drawFlyers(ctx);
    this.drawParticles(ctx);
    this.drawPops(ctx);
    ctx.restore();
  }

  private drawPyramid(ctx: CanvasRenderingContext2D, s: GameState, L: CairnLayout): void {
    // back-to-front: base row first, tip last (a higher row covers the top of the one below)
    for (let r = this.pyH - 1; r >= 0; r--) {
      for (const st of L.stones) {
        if (st.row !== r) continue;
        if (!s.present[st.id]) continue;
        const exposed = isExposed(s, st.id);
        const legal = exposed && canTake(s, st.id);
        this.drawStone(ctx, st, s.board.stones[st.id].rank, s.board.stones[st.id].hue, { exposed, legal, selected: this.selected === st.id });
      }
    }
  }

  private drawStone(ctx: CanvasRenderingContext2D, rect: StoneRect | Slot, rank: number, hue: number, o: { exposed: boolean; legal: boolean; selected?: boolean }): void {
    const lift = o.legal ? 2 + Math.sin(this.pulse * 4) * 1.2 : 0;
    const x = rect.cx - rect.w / 2;
    const y = rect.cy - rect.h / 2 - lift;
    const w = rect.w;
    const h = rect.h;
    const r = Math.max(4, w * 0.16);

    if (o.legal) {
      ctx.save();
      ctx.shadowColor = PALETTE.legal;
      ctx.shadowBlur = 14;
      this.roundRectPath(ctx, x, y, w, h, r);
      ctx.fillStyle = PALETTE.legal;
      ctx.fill();
      ctx.restore();
    }

    // body
    const face = STONE_HUES[hue % 4];
    this.roundRectPath(ctx, x, y, w, h, r);
    const grad = ctx.createLinearGradient(0, y, 0, y + h);
    grad.addColorStop(0, this.lighten(face, 0.1));
    grad.addColorStop(1, this.darken(face, 0.12));
    ctx.fillStyle = grad;
    ctx.fill();
    // rim
    ctx.lineWidth = Math.max(1.2, w * 0.05);
    ctx.strokeStyle = o.legal ? PALETTE.legal : 'rgba(30,20,6,0.55)';
    ctx.stroke();
    // keyboard cursor ring
    if (o.selected) {
      this.roundRectPath(ctx, x - 3, y - 3, w + 6, h + 6, r + 3);
      ctx.strokeStyle = PALETTE.hand;
      ctx.lineWidth = 2.5;
      ctx.stroke();
    }

    // rune
    const label = rankLabel(rank);
    ctx.fillStyle = PALETTE.ink;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const fs = h * (label.length > 1 ? 0.42 : 0.52);
    ctx.font = `700 ${fs}px ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
    ctx.fillText(label, rect.cx, rect.cy - lift);

    // shadow scrim on blocked stones so exposed ones pop (a lightness cue, not colour)
    if (!o.exposed) {
      this.roundRectPath(ctx, x, y, w, h, r);
      ctx.fillStyle = 'rgba(9,6,18,0.5)';
      ctx.fill();
    }
  }

  private drawStrip(ctx: CanvasRenderingContext2D, s: GameState, L: CairnLayout): void {
    // hand stone
    const hand = L.hand;
    ctx.save();
    ctx.shadowColor = PALETTE.hand;
    ctx.shadowBlur = 16;
    this.roundRectPath(ctx, hand.cx - hand.w / 2, hand.cy - hand.h / 2, hand.w, hand.h, hand.w * 0.16);
    ctx.fillStyle = PALETTE.hand;
    ctx.globalAlpha = 0.5;
    ctx.fill();
    ctx.restore();
    this.drawStone(ctx, hand, s.hand, s.hue, { exposed: true, legal: false });

    // scree pile
    const stock = L.stock;
    const left = s.board.drawBudget - s.draws;
    const avail = left > 0 && s.stockIdx < s.board.stock.length;
    const backs = Math.min(3, Math.max(1, Math.min(left, s.board.stock.length - s.stockIdx)));
    for (let i = backs - 1; i >= 0; i--) {
      const off = i * Math.max(2, stock.w * 0.05);
      const x = stock.cx - stock.w / 2 - off;
      const y = stock.cy - stock.h / 2 - off;
      this.roundRectPath(ctx, x, y, stock.w, stock.h, stock.w * 0.16);
      const g = ctx.createLinearGradient(0, y, 0, y + stock.h);
      g.addColorStop(0, avail ? '#4b3f70' : '#2a2440');
      g.addColorStop(1, avail ? '#332a52' : '#201b34');
      ctx.fillStyle = g;
      ctx.fill();
      ctx.lineWidth = 1.4;
      ctx.strokeStyle = 'rgba(12,8,22,0.6)';
      ctx.stroke();
    }
    // draws-left badge
    ctx.fillStyle = avail ? PALETTE.text : PALETTE.textMuted;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `700 ${stock.h * 0.3}px ui-sans-serif, system-ui, sans-serif`;
    ctx.fillText(String(Math.max(0, left)), stock.cx, stock.cy);
    ctx.fillStyle = PALETTE.textMuted;
    ctx.font = `600 ${stock.h * 0.16}px ui-sans-serif, system-ui, sans-serif`;
    ctx.fillText('draws', stock.cx, stock.cy + stock.h * 0.34);
    if (this.drawFlash > 0 && avail) {
      ctx.strokeStyle = `rgba(85,216,232,${this.drawFlash})`;
      ctx.lineWidth = 2;
      this.roundRectPath(ctx, stock.cx - stock.w / 2, stock.cy - stock.h / 2, stock.w, stock.h, stock.w * 0.16);
      ctx.stroke();
    }
  }

  private drawFlyers(ctx: CanvasRenderingContext2D): void {
    for (const f of this.flyers) {
      const k = Math.min(1, f.t / f.dur);
      const e = 1 - (1 - k) * (1 - k);
      const cx = f.fromX + (f.toX - f.fromX) * e;
      const cy = f.fromY + (f.toY - f.fromY) * e;
      const w = f.w;
      const h = f.h;
      const r = w * 0.16;
      this.roundRectPath(ctx, cx - w / 2, cy - h / 2, w, h, r);
      ctx.fillStyle = STONE_HUES[f.hue % 4];
      ctx.fill();
      ctx.fillStyle = PALETTE.ink;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const label = rankLabel(f.rank);
      ctx.font = `700 ${h * (label.length > 1 ? 0.42 : 0.52)}px ui-sans-serif, system-ui, sans-serif`;
      ctx.fillText(label, cx, cy);
    }
  }

  private drawParticles(ctx: CanvasRenderingContext2D): void {
    for (const p of this.particles) {
      ctx.globalAlpha = Math.max(0, 1 - p.life / p.max);
      ctx.fillStyle = p.col;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  private drawPops(ctx: CanvasRenderingContext2D): void {
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (const p of this.pops) {
      ctx.globalAlpha = Math.max(0, Math.min(1, p.life));
      ctx.fillStyle = p.col;
      const size = 20 + (1 - p.life) * 14;
      ctx.font = `800 ${size}px ui-sans-serif, system-ui, sans-serif`;
      ctx.fillText(p.text, p.x, p.y - (1 - p.life) * 26);
    }
    ctx.globalAlpha = 1;
  }

  // ── shape + colour helpers ──────────────────────────────────────────────
  private roundRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
    const rr = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + rr, y);
    ctx.arcTo(x + w, y, x + w, y + h, rr);
    ctx.arcTo(x + w, y + h, x, y + h, rr);
    ctx.arcTo(x, y + h, x, y, rr);
    ctx.arcTo(x, y, x + w, y, rr);
    ctx.closePath();
  }
  private lighten(hex: string, amt: number): string {
    return this.mix(hex, '#ffffff', amt);
  }
  private darken(hex: string, amt: number): string {
    return this.mix(hex, '#000000', amt);
  }
  private mix(a: string, b: string, t: number): string {
    const pa = this.parse(a);
    const pb = this.parse(b);
    const c = pa.map((v, i) => Math.round(v + (pb[i] - v) * t));
    return `rgb(${c[0]},${c[1]},${c[2]})`;
  }
  private parse(hex: string): number[] {
    const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
    return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : [0, 0, 0];
  }

  // ── input support (CSS px within the host) ─────────────────────────────
  /** Topmost present stone whose rect contains the point (front rows first). */
  stoneAt(cssX: number, cssY: number, s: GameState): number | null {
    const L = this.layout;
    if (!L) return null;
    for (let r = 0; r < this.pyH; r++) {
      for (const st of L.stones) {
        if (st.row !== r || !s.present[st.id]) continue;
        if (this.hit(st, cssX, cssY)) return st.id;
      }
    }
    return null;
  }
  stockAt(cssX: number, cssY: number): boolean {
    const L = this.layout;
    if (!L) return false;
    return this.hit(L.stock, cssX, cssY, 1.15);
  }
  private hit(rect: { cx: number; cy: number; w: number; h: number }, x: number, y: number, pad = 1): boolean {
    const hw = Math.max((rect.w * pad) / 2, 22);
    const hh = Math.max((rect.h * pad) / 2, 22);
    return Math.abs(x - rect.cx) <= hw && Math.abs(y - rect.cy) <= hh;
  }
  stoneCenterCss(id: number): { x: number; y: number } | null {
    const st = this.layout?.stones[id];
    return st ? { x: st.cx, y: st.cy } : null;
  }
  exposedCenters(s: GameState): Array<{ id: number; x: number; y: number }> {
    const L = this.layout;
    if (!L) return [];
    return L.stones.filter((st) => isExposed(s, st.id)).map((st) => ({ id: st.id, x: st.cx, y: st.cy }));
  }

  // ── contrast probes (device pixels) ────────────────────────────────────
  stonePixel(id: number): { x: number; y: number } | null {
    const st = this.layout?.stones[id];
    return st ? { x: st.cx * this.dpr, y: st.cy * this.dpr } : null;
  }
  handPixel(): { x: number; y: number } | null {
    const h = this.layout?.hand;
    return h ? { x: h.cx * this.dpr, y: h.cy * this.dpr } : null;
  }
  emptyPixel(): { x: number; y: number } {
    return { x: this.cw * 0.03 * this.dpr, y: this.ch * 0.02 * this.dpr };
  }

  destroy(): void {
    this.particles = [];
    this.flyers = [];
    this.pops = [];
  }
}
